import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchTransactions } from "../store/transactionSlice";
import { api } from "../services/api";

import { PDFDownloadLink } from "@react-pdf/renderer";
import StatementPDF from "../components/pdf/StatementPDF";

import { Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";

export default function Statements() {
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const { items, loading } = useSelector((s) => s.transactions);

  const [projectName, setProjectName] = useState("HB Height");

  const [from, setFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));

  useEffect(() => {
    // Load transactions for statement (large limit)
    dispatch(fetchTransactions({ projectId, params: { page: 1, limit: 500, from, to } }));
  }, [dispatch, projectId, from, to]);

  useEffect(() => {
    // Optional: load project name if API exists /projects/:id
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        setProjectName(data?.project?.name || "HB Height");
      } catch {
        // ignore if route not available
      }
    })();
  }, [projectId]);

  const totals = useMemo(() => {
    const totalAmount = items.reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalPaid = items.reduce((s, r) => s + Number(r.paidAmount || 0), 0);
    const totalRemaining = Math.max(totalAmount - totalPaid, 0);
    return { totalAmount, totalPaid, totalRemaining };
  }, [items]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Statement
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Download a professional PDF statement for selected dates
          </Typography>
        </Box>

        <PDFDownloadLink
          document={<StatementPDF projectName={projectName} from={from} to={to} rows={items} />}
          fileName={`${projectName}_statement_${from}_to_${to}.pdf`}
          style={{ textDecoration: "none" }}
        >
          {({ loading: pdfLoading }) => (
            <Button
              variant="contained"
              sx={{ borderRadius: 2, fontWeight: 900 }}
              disabled={pdfLoading || loading}
            >
              {pdfLoading ? "Preparing PDF..." : "Download PDF"}
            </Button>
          )}
        </PDFDownloadLink>
      </Stack>

      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography fontWeight={900}>
                  {totals.totalAmount.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Paid
                </Typography>
                <Typography fontWeight={900}>
                  {totals.totalPaid.toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Remaining
                </Typography>
                <Typography fontWeight={900}>
                  {totals.totalRemaining.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Typography variant="body2" color="text.secondary">
          Records found: {items.length}
        </Typography>
      )}
    </Box>
  );
}
